import { useMemo } from 'react';

import { capitalizeWords } from '@/lib/format';
import { SUBJECT_GROUP_LABEL, subjectGroupDot, subjectGroupOf, type SubjectGroup } from '../subjectTheme';

export interface TimetableLegendProps {
  /** The subject names actually placed in the week on screen. */
  subjectNames: string[];
}

/**
 * What the colours on the grid mean — for this institute's subjects only.
 *
 * Built from what is on the grid rather than from a fixed list, which is what
 * it was: six categories printed whether or not the institute taught any of
 * them, and no way for a seventh to appear. A school that teaches only Telugu,
 * English and Maths now gets a three-item legend.
 */
export function TimetableLegend({ subjectNames }: TimetableLegendProps) {
  const groups = useMemo(() => {
    const seen = new Map<SubjectGroup, string[]>();

    for (const name of subjectNames) {
      const group = subjectGroupOf(name);
      const names = seen.get(group);
      if (names) {
        if (!names.includes(name)) names.push(name);
      } else {
        seen.set(group, [name]);
      }
    }

    return [...seen.entries()]
      .map(([group, names]) => ({ group, names: names.sort((a, b) => a.localeCompare(b)) }))
      .sort((a, b) => SUBJECT_GROUP_LABEL[a.group].localeCompare(SUBJECT_GROUP_LABEL[b.group]));
  }, [subjectNames]);

  if (groups.length === 0) return null;

  return (
    <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-bold text-slate-600 dark:text-slate-400 px-2 py-1 print:text-black print:mt-2">
      {groups.map(({ group, names }) => (
        <li key={group} className="flex items-center gap-2" title={names.map((n) => capitalizeWords(n)).join(', ')}>
          <span className={`h-2.5 w-2.5 rounded-full inline-block ${subjectGroupDot(group)}`} />
          <span>{SUBJECT_GROUP_LABEL[group]}</span>
        </li>
      ))}
    </ul>
  );
}
